import React from 'react';
import { Button, Divider, Select, Space } from 'antd';
import TipTap from './TipTap';
import ProjectTypeExamples from './Tables/ProjectTypeExamples';
import ActivityType from './Figures/ActivityType';
import { createSection } from '../constants';
import { v4 as uuidv4 } from 'uuid';
import Others from './Others';

export interface ContentItem {
  id: string;
  type: string; // content, table, figure, or other
  data: any;
  count?: number;
  isDisabled?: boolean;
}

interface SectionsProps {
  data: any;
  setData: (path: string, value: any) => void;
  sectionKey: string;
  readonly?: boolean;
  dataKey: 'riskDrivers' | 'description';
  containerId?: string; // ID of the parent container (riskDriver ID for riskDrivers dataKey)
}

/**
 * Generic Sections component that can handle both riskDrivers and description content
 * For riskDrivers, pass dataKey='riskDrivers' and containerId=riskDriver.id
 * For description, pass dataKey='description' (no containerId needed)
 */
const Sections: React.FC<SectionsProps> = ({
  data,
  setData,
  sectionKey,
  readonly = false,
  dataKey,
  containerId
}) => {
  // Get the content items based on dataKey and containerId
  const getContentItems = (): ContentItem[] => {
    if (dataKey === 'riskDrivers') {
      if (!containerId) {
        console.error('containerId is required for riskDrivers dataKey');
        return [];
      }

      const riskDriver = data.riskDrivers.find((driver: any) => driver.id === containerId);
      return riskDriver?.data || [];
    } else if (dataKey === 'description') {
      // If handling description, we need to create a content item structure if it doesn't exist
      if (!Array.isArray(data[dataKey])) {
        // Initialize as array if it's not already
        const initialContent = [
          {
            id: uuidv4(),
            type: 'content',
            data: data[dataKey] || {
              type: 'doc',
              content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }]
            }
          }
        ];
        setData(`${sectionKey}.${dataKey}`, initialContent);
        return initialContent;
      }
      return data[dataKey];
    }

    return [];
  };

  const contentItems = getContentItems();

  const updateContent = (updatedContent: ContentItem[]) => {
    if (dataKey === 'riskDrivers') {
      const updatedRiskDrivers = data.riskDrivers.map((driver: any) => {
        if (driver.id === containerId) {
          return {
            ...driver,
            data: updatedContent
          };
        }
        return driver;
      });
      setData(`${sectionKey}.riskDrivers`, updatedRiskDrivers);
    } else {
      setData(`${sectionKey}.${dataKey}`, updatedContent);
    }
  };

  const handleTypeChange = (contentItem: ContentItem, newType: string) => {
    if (readonly) return;

    const updatedContent = contentItems.map((item) => {
      if (item.id === contentItem.id) {
        const count = contentItems.filter((i) => i.type === 'figure').length;
        const getDefaultData = () => {
          switch (newType) {
            case 'content':
              return {
                type: 'doc',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }]
              };
            case 'table':
              return [];
            case 'figure':
              return {
                title: '',
                id: '',
                description: null
              };
            case 'other':
              return {
                title: '',
                id: ''
              };
            default:
              return null;
          }
        };
        return {
          ...item,
          type: newType,
          data: getDefaultData(),
          ...(newType === 'figure' ? { count: count + 1 } : {})
        };
      }
      return item;
    });

    updateContent(updatedContent);
  };

  const handleAddSectionAbove = (contentItemIndex: number) => {
    if (readonly) return;

    const updatedContent = [
      ...contentItems.slice(0, contentItemIndex),
      createSection('content'),
      ...contentItems.slice(contentItemIndex)
    ];

    updateContent(updatedContent);
  };

  const handleAddSection = () => {
    if (readonly) return;

    const updatedContent = [...contentItems, createSection('content')];
    updateContent(updatedContent);
  };

  const handleRemoveSection = (contentItemId: string) => {
    if (readonly) return;

    const updatedContent = contentItems.filter((item) => item.id !== contentItemId);
    updateContent(updatedContent);
  };

  const handleContentChange = (contentItem: ContentItem, contentItemIndex: number, value: any) => {
    if (readonly) return;

    const updatedContent = contentItems.map((item) => {
      if (item.id === contentItem.id) {
        return { ...item, data: value };
      }
      return item;
    });

    updateContent(updatedContent);
  };

  return (
    <>
      {contentItems.map((contentItem, contentItemIndex) => (
        <Space
          direction="vertical"
          style={{ width: '100%', marginBottom: '16px' }}
          key={contentItem.id}>
          <Space style={{ width: '100%', marginBottom: '8px' }}>
            <Select
              value={contentItem.type}
              onChange={(value) => handleTypeChange(contentItem, value)}
              style={{ width: 220 }}
              disabled={readonly || contentItem.isDisabled}>
              <Select.Option value="content">Paragraph</Select.Option>
              <Select.Option value="table">Risk Level Table</Select.Option>
              <Select.Option value="figure">Chart</Select.Option>
              <Select.Option value="other">Others (Custom table, maps, etc.)</Select.Option>
            </Select>

            <Button
              type="primary"
              onClick={() => handleAddSectionAbove(contentItemIndex)}
              disabled={readonly}>
              Add Section above
            </Button>
            <Button
              danger
              onClick={() => handleRemoveSection(contentItem.id)}
              disabled={readonly || contentItems.length <= 1 || contentItem.isDisabled}>
              Remove Section
            </Button>
          </Space>

          {contentItem.type === 'content' ? (
            <TipTap
              value={contentItem.data}
              onChange={(value) => handleContentChange(contentItem, contentItemIndex, value)}
              readonly={readonly || contentItem.isDisabled}
            />
          ) : contentItem.type === 'table' ? (
            <ProjectTypeExamples
              setData={setData}
              data={data}
              riskDriverId={containerId}
              contentItemId={contentItem.id}
              sectionKey={sectionKey}
              readonly={readonly || contentItem.isDisabled}
              dataKey={dataKey}
            />
          ) : contentItem.type === 'figure' ? (
            <ActivityType
              setData={setData}
              data={data}
              riskDriverId={containerId}
              contentItemId={contentItem.id}
              dataKey={dataKey}
              sectionKey={sectionKey}
              readonly={readonly || contentItem.isDisabled}
            />
          ) : (
            <Others
              setData={setData}
              data={data}
              riskDriverId={containerId}
              contentItemId={contentItem.id}
              dataKey={dataKey}
              sectionKey={sectionKey}
              readonly={readonly || contentItem.isDisabled}
            />
          )}

          {contentItemIndex < contentItems.length - 1 && (
            <Divider style={{ margin: '16px 0' }} dashed />
          )}
        </Space>
      ))}

      <Button
        type="primary"
        onClick={handleAddSection}
        style={{ marginTop: '10px' }}
        disabled={readonly}>
        Add Section
      </Button>
    </>
  );
};

export default Sections;
